import {
  ALLOWED_MARKS,
  ALLOWED_NODES,
  JSONNode,
  NodeGroup,
  VALID_COLUMN_LAYOUTS,
  VALID_FIELD_TOKEN_IDS,
} from "./schema";

/**
 * Template normalizer
 * --------------------
 * Reduces arbitrary/foreign TipTap-shaped JSON down to the editor's supported
 * node + mark vocabulary, so it is safe to load into the editor and to feed to
 * the server pdfmake converter.
 *
 * WHERE THIS IS USED: this is an authoring-time / import utility, NOT part of the
 * live editing path. Content the running editor produces is already valid. The
 * normalizer guards the *import* pipeline:
 *
 *     claude.ai/design HTML
 *        -> TipTap JSON   (via the editor schema, e.g. generateJSON(html, ExtensionKit),
 *                          which already discards tags our schema doesn't recognise)
 *        -> normalizeTemplateContent(...)   (this file: field-token validity,
 *                                            heading-level clamping, mark/attr
 *                                            stripping, structural repair)
 *
 * The HTML->JSON step is intentionally left to the call site (it needs the editor
 * extensions + a DOM); this module is the pure, dependency-free, testable core.
 * docx or other sources would converge on the same JSON entry point.
 */

const isObject = (v: unknown): v is Record<string, any> => !!v && typeof v === "object";

/** Normalize a list of sibling nodes within a given context. */
function normalizeChildren(nodes: unknown, ctx: NodeGroup): JSONNode[] {
  if (!Array.isArray(nodes)) return [];
  const out: JSONNode[] = [];
  for (const node of nodes) out.push(...normalizeNode(node, ctx));
  return out;
}

/** Unwrap an unsupported node by promoting its children into the current context. */
function unwrap(node: JSONNode, ctx: NodeGroup): JSONNode[] {
  if (Array.isArray(node.content)) return normalizeChildren(node.content, ctx);
  if (ctx === "inline" && typeof node.text === "string" && node.text) return [{ type: "text", text: node.text }];
  return [];
}

function pickAttrs(src: unknown, ruleAttrs: Record<string, unknown>): Record<string, any> {
  const out: Record<string, any> = {};
  const source = isObject(src) ? src : {};
  for (const key of Object.keys(ruleAttrs)) {
    const def = ruleAttrs[key];
    if (source[key] !== undefined) out[key] = source[key];
    else if (def !== undefined) out[key] = def;
  }
  return out;
}

function pickKnown(src: unknown, keys: string[]): Record<string, any> {
  const out: Record<string, any> = {};
  const source = isObject(src) ? src : {};
  for (const key of keys) if (source[key] !== undefined) out[key] = source[key];
  return out;
}

function clampHeadingLevel(raw: unknown): number {
  const n = typeof raw === "number" ? raw : parseInt(String(raw ?? ""), 10);
  if (!Number.isFinite(n)) return 1;
  return Math.min(3, Math.max(1, Math.trunc(n)));
}

function validLayout(raw: unknown): string {
  return (VALID_COLUMN_LAYOUTS as readonly string[]).includes(raw as string) ? (raw as string) : "two-column";
}

function normalizeMarks(marks: unknown): JSONNode["marks"] {
  if (!Array.isArray(marks)) return [];
  const seen = new Set<string>();
  const out: NonNullable<JSONNode["marks"]> = [];
  for (const m of marks) {
    if (!isObject(m) || typeof m.type !== "string") continue;
    const allowedAttrs = ALLOWED_MARKS[m.type];
    if (!allowedAttrs || seen.has(m.type)) continue;
    seen.add(m.type);
    const mark: { type: string; attrs?: Record<string, any> } = { type: m.type };
    if (allowedAttrs.length) {
      const attrs = pickKnown(m.attrs, allowedAttrs);
      if (Object.keys(attrs).length) mark.attrs = attrs;
    }
    out.push(mark);
  }
  return out;
}

function normalizeText(node: JSONNode): JSONNode[] {
  if (typeof node.text !== "string" || node.text === "") return [];
  const marks = normalizeMarks(node.marks);
  const out: JSONNode = { type: "text", text: node.text };
  if (marks && marks.length) out.marks = marks;
  return [out];
}

function normalizeMention(node: JSONNode): JSONNode[] {
  const id = node.attrs?.id;
  const label = node.attrs?.label;
  if (typeof id === "string" && VALID_FIELD_TOKEN_IDS.has(id)) {
    return [{ type: "mention", attrs: { id, label: typeof label === "string" && label ? label : id } }];
  }
  // Invalid/unknown token: keep the human-readable text rather than lose content.
  const fallback = typeof label === "string" && label ? label : typeof id === "string" ? id : "";
  return fallback ? [{ type: "text", text: fallback }] : [];
}

function buildHeading(node: JSONNode): JSONNode[] {
  const attrs = pickAttrs(node.attrs, ALLOWED_NODES.heading.attrs);
  attrs.level = clampHeadingLevel(node.attrs?.level);
  const out: JSONNode = { type: "heading", attrs };
  const content = normalizeChildren(node.content, "inline");
  if (content.length) out.content = content;
  return [out];
}

function normalizeColumns(node: JSONNode): JSONNode[] {
  const children = normalizeChildren(node.content, "block");
  const columns = children.filter((c) => c.type === "column");
  if (columns.length === 2) {
    return [{ type: "columns", attrs: { layout: validLayout(node.attrs?.layout) }, content: columns }];
  }
  // Can't form a valid 2-column block — unwrap inner content so nothing is lost.
  return children.flatMap((c) => (c.type === "column" ? c.content ?? [] : [c]));
}

function normalizeList(node: JSONNode, type: "bulletList" | "orderedList"): JSONNode[] {
  const items = normalizeChildren(node.content, "block").filter((c) => c.type === "listItem");
  if (!items.length) return [];
  const out: JSONNode = { type };
  if (type === "orderedList") out.attrs = pickAttrs(node.attrs, ALLOWED_NODES.orderedList.attrs);
  out.content = items;
  return [out];
}

/** A table with no rows left after normalization is invalid against the tableRow+ schema — drop it, same as an empty list. */
function normalizeTable(node: JSONNode): JSONNode[] {
  const rows = normalizeChildren(node.content, "block").filter((c) => c.type === "tableRow");
  if (!rows.length) return [];
  const attrs = pickAttrs(node.attrs, ALLOWED_NODES.table.attrs);
  const out: JSONNode = { type: "table", content: rows };
  if (Object.keys(attrs).length) out.attrs = attrs;
  return [out];
}

/** Nodes whose schema requires non-empty block content; seed an empty paragraph if missing. */
const REQUIRES_BLOCK_CONTENT = new Set(["column", "listItem", "tableCell", "tableHeader"]);

function buildGeneric(node: JSONNode, type: string): JSONNode[] {
  const rule = ALLOWED_NODES[type];
  const out: JSONNode = { type };
  const attrs = pickAttrs(node.attrs, rule.attrs);
  if (Object.keys(attrs).length) out.attrs = attrs;
  if (rule.childCtx) {
    let content = normalizeChildren(node.content, rule.childCtx);
    if (content.length === 0 && REQUIRES_BLOCK_CONTENT.has(type)) content = [{ type: "paragraph" }];
    if (content.length) out.content = content;
  }
  return [out];
}

function normalizeNode(node: unknown, ctx: NodeGroup): JSONNode[] {
  if (!isObject(node)) return [];
  const type = typeof node.type === "string" ? node.type : undefined;
  if (!type) return [];

  const rule = ALLOWED_NODES[type];
  if (!rule) return unwrap(node, ctx);

  if (rule.group !== ctx) {
    if (rule.group === "inline" && ctx === "block") {
      // Stray inline content at block level — wrap it in a paragraph.
      const inline = normalizeNode(node, "inline");
      return inline.length ? [{ type: "paragraph", attrs: { textAlign: "left" }, content: inline }] : [];
    }
    // Block node in an inline context — unwrap.
    return unwrap(node, ctx);
  }

  switch (type) {
    case "text":
      return normalizeText(node);
    case "mention":
      return normalizeMention(node);
    case "heading":
      return buildHeading(node);
    case "columns":
      return normalizeColumns(node);
    case "bulletList":
    case "orderedList":
      return normalizeList(node, type);
    case "table":
      return normalizeTable(node);
    default:
      return buildGeneric(node, type);
  }
}

function coerceToNodeArray(input: unknown): JSONNode[] {
  if (Array.isArray(input)) return input;
  if (isObject(input)) {
    if (Array.isArray(input.content)) return input.content; // doc / wrapper node
    if (typeof input.type === "string") return [input as JSONNode]; // single node
  }
  return [];
}

/**
 * Normalize page content (an array of block nodes, matching the shape of
 * `defaultCoverPage`) down to the supported vocabulary.
 *
 * Accepts: an array of nodes, a `{ type: "doc", content: [...] }` document, or a
 * single node. Always returns a clean array of block nodes.
 */
export function normalizeTemplateContent(input: unknown): JSONNode[] {
  return normalizeChildren(coerceToNodeArray(input), "block");
}

/** Convenience: normalize the `content` of each page in a pages array. */
export function normalizeTemplatePages<T extends { content?: unknown }>(pages: T[]): T[] {
  if (!Array.isArray(pages)) return [];
  return pages.map((page) => ({ ...page, content: normalizeTemplateContent(page.content) }));
}
